import { PeriodDTO, PeriodDetailDTO, RawPeriod, RawPeriodDetail } from '@/types/period';

/**
 * Map RawPeriodDetail to PeriodDetail format
 */
export const mapPeriodDetail = (detail: RawPeriodDetail) => ({
  id: detail.ID || detail.id || '',
  namBSCID: detail.NamBSCID || detail.namBSCID || '',
  year: detail.Year || detail.year || 0,
  batDau: detail.BatDau || detail.batDau || '',
  ketThuc: detail.KetThuc || detail.ketThuc || '',
  site: detail.Site || detail.site || '',
  ngayTao: detail.NgayTao || detail.ngayTao || null,
  nguoiTao: detail.NguoiTao || detail.nguoiTao || null,
  nguoiCapNhat: detail.NguoiCapNhat || detail.nguoiCapNhat || null,
  ngayCapNhat: detail.NgayCapNhat || detail.ngayCapNhat || null,
});

/**
 * Map RawPeriod to PeriodDTO format
 */
export const mapRawPeriodToDTO = (period: RawPeriod): PeriodDTO => ({
  id: period.ID || period.id || '',
  ten: period.Ten || period.ten || '',
  batDau: period.BatDau || period.batDau || '',
  ketThuc: period.KetThuc || period.ketThuc || '',
  namTaiChinh: period.NamTaiChinh ?? period.namTaiChinh ?? false,
  periodDetails: (period.PeriodDetails || period.periodDetails || []).map(mapPeriodDetail),
});

/**
 * Sort PeriodDTO array by batDau (ascending), fallback to ten if batDau is missing or equal
 */
export const sortPeriodsByBatDau = (periods: PeriodDTO[]): PeriodDTO[] => {
  return [...periods].sort((a, b) => {
    const aHasDate = !!a.batDau;
    const bHasDate = !!b.batDau;

    // Nếu cả hai đều có batDau, so sánh theo date
    if (aHasDate && bHasDate) {
      const dateA = new Date(a.batDau).getTime();
      const dateB = new Date(b.batDau).getTime();
      if (dateA !== dateB) return dateA - dateB;
    } else if (aHasDate && !bHasDate) {
      return -1; // a có batDau, b không có -> a đứng trước
    } else if (!aHasDate && bHasDate) {
      return 1; // a không có batDau, b có -> b đứng trước
    }

    // Nếu cả hai đều không có batDau hoặc batDau bằng nhau, sort theo ten
    return (a.ten || '').toLowerCase().localeCompare((b.ten || '').toLowerCase());
  });
};

/**
 * Map and sort RawPeriod array to PeriodDTO array
 */
export const mapAndSortPeriods = (periods: RawPeriod[]): PeriodDTO[] => {
  const mappedPeriods = periods.map(mapRawPeriodToDTO);
  return sortPeriodsByBatDau(mappedPeriods);
};

function sortPeriodDetailsByYear(period: PeriodDTO): PeriodDTO {
  const details = [...(period.periodDetails || [])].filter((d) => d.year > 0);
  if (details.length > 0) {
    details.sort((a, b) => a.year - b.year || new Date(a.batDau).getTime() - new Date(b.batDau).getTime());
    return { ...period, periodDetails: details };
  }

  const start = period.batDau ? new Date(period.batDau) : null;
  const end = period.ketThuc ? new Date(period.ketThuc) : null;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { ...period, periodDetails: [] };
  }

  const generated: PeriodDetailDTO[] = [];
  for (let year = start.getFullYear(); year <= end.getFullYear(); year += 1) {
    generated.push({
      namBSCID: period.id,
      year,
      batDau: period.batDau,
      ketThuc: period.ketThuc,
      id: `${period.id}-${year}`,
      ngayTao: null,
      nguoiTao: null,
      nguoiCapNhat: null,
      ngayCapNhat: null,
      site: '',
    });
  }
  return { ...period, periodDetails: generated };
}

/**
 * Map `/v1/period/list` rồi sort giống bảng Giai đoạn (theo batDau, trên xuống).
 */
export function parsePeriodListPayload(data: unknown): PeriodDTO[] {
  if (!data) return [];
  const items = Array.isArray(data)
    ? data
    : Object.values(data as Record<string, unknown>);
  const mapped = items.map((item) => mapRawPeriodToDTO(item as RawPeriod));
  return sortPeriodsByBatDau(mapped).map(sortPeriodDetailsByYear);
}

/**
 * Chuyển text tiếng Việt thành slug an toàn cho URL
 */
export const toSlug = (value: string): string =>
  encodeURIComponent(
    (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D')
      .replace(/\s+/g, '-')
      .toLowerCase(),
  );

  /**
 * Lấy màu dựa trên phần trăm
 * @param value - Phần trăm
 * @returns Màu tương ứng
 */
export const getPercentColor = (value: number): string => {
  const percent = value / 100;
  if (percent <= 0.333) return '#ef4444';
  if (percent <= 0.667) return '#fbbf24';
  return '#22c55e';
};